import { api, getAccessToken } from "./api";
import type {
  DocumentVersion,
  DocumentHistoryItem,
  DocumentType,
  GenerateResponse,
} from "../types";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export type ScoresEvent = { scores: { alignment_score: number; success_prob: number } };

export const documentsService = {
  generate: (jobId: string, docType?: DocumentType) =>
    api.post<GenerateResponse>(`/api/jobs/${jobId}/generate`, docType ? { doc_type: docType } : {}),

  getLatest: (jobId: string, docType: DocumentType) =>
    api.get<DocumentVersion>(`/api/jobs/${jobId}/documents/${docType}`),

  save: (jobId: string, docType: DocumentType, content: string, instruction?: string) =>
    api.put<DocumentVersion>(`/api/jobs/${jobId}/documents/${docType}`, { content, instruction }),

  getHistory: (jobId: string, docType: DocumentType) =>
    api.get<DocumentHistoryItem[]>(`/api/jobs/${jobId}/documents/${docType}/history`),

  restore: (jobId: string, docType: DocumentType, versionId: string) =>
    api.post<DocumentVersion>(
      `/api/jobs/${jobId}/documents/${docType}/restore/${versionId}`
    ),

  chat: (jobId: string, docType: DocumentType, instruction: string) =>
    api.post<DocumentVersion>(`/api/jobs/${jobId}/documents/${docType}/chat`, {
      instruction,
    }),

  exportPdfUrl: (jobId: string, docType: DocumentType) =>
    `/api/jobs/${jobId}/documents/${docType}/export/pdf`,

  exportVersionPdfUrl: (jobId: string, docType: DocumentType, versionId: string) =>
    `/api/jobs/${jobId}/documents/${docType}/versions/${versionId}/export/pdf`,

  async *streamGenerate(jobId: string, docType: DocumentType): AsyncGenerator<string> {
    yield* _streamSse(
      `${BASE_URL}/api/jobs/${jobId}/documents/${docType}/stream/generate`,
      { method: "POST" }
    );
  },

  async *streamRefine(
    jobId: string,
    docType: DocumentType,
    instruction: string,
    currentContent: string
  ): AsyncGenerator<string> {
    yield* _streamSse(
      `${BASE_URL}/api/jobs/${jobId}/documents/${docType}/stream/refine`,
      {
        method: "POST",
        body: JSON.stringify({ instruction, current_content: currentContent }),
      }
    );
  },

  async *streamAnalyse(jobId: string): AsyncGenerator<string | ScoresEvent> {
    yield* _streamSseAnalyse(`${BASE_URL}/api/jobs/${jobId}/analyse/stream`);
  },

  async downloadPdf(url: string): Promise<void> {
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const res = await fetch(`${BASE_URL}${url}`, { headers });
    if (!res.ok) {
      throw new Error(`PDF download failed: ${res.status} ${res.statusText}`);
    }
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = url.split("/").pop() ?? "document.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  },
};

// ── SSE helpers ───────────────────────────────────────────────────────────────

async function* _streamSse(
  url: string,
  init: RequestInit
): AsyncGenerator<string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getAccessToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(url, { ...init, headers, credentials: "include" });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ?? detail;
    } catch {}
    throw new Error(`${res.status}: ${detail}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (data === "[DONE]") return;
      try {
        const parsed = JSON.parse(data);
        if (typeof parsed.chunk === "string") {
          yield parsed.chunk;
        }
      } catch {}
    }
  }
}

async function* _streamSseAnalyse(
  url: string
): AsyncGenerator<string | ScoresEvent> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getAccessToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(url, { method: "POST", headers, credentials: "include" });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = await res.json();
      detail = body.detail ?? detail;
    } catch {}
    throw new Error(`${res.status}: ${detail}`);
  }

  const reader = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (data === "[DONE]") return;
      try {
        const parsed = JSON.parse(data);
        if (typeof parsed.chunk === "string") {
          yield parsed.chunk;
        } else if (parsed.scores !== undefined) {
          yield { scores: parsed.scores } as ScoresEvent;
        }
      } catch {}
    }
  }
}
